import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2, Plus, Trash2, UserPlus, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  useAssignStaff,
  useEmployees,
  useRemoveStaffAssignment,
  useStaffAssignments,
} from '../../services/httpServices/queries';
import type { StaffAssignmentRole } from '../../services/httpServices/ipdService';
import { SearchableSelect, type Option } from './searchable-select';
import { ConfirmDialog } from './confirm-dialog';
import { formatDateLabel } from '../../utils/dateFormat';

interface AdmissionCareTeamModalProps {
  open: boolean;
  admissionId: string;
  onClose: () => void;
}

const ROLE_OPTIONS: Array<{ value: StaffAssignmentRole; label: string }> = [
  { value: 'PRIMARY_DOCTOR', label: 'Primary doctor' },
  { value: 'DUTY_DOCTOR', label: 'Duty doctor' },
  { value: 'NURSE', label: 'Nurse' },
  { value: 'SURGEON', label: 'Surgeon' },
  { value: 'ANESTHESIOLOGIST', label: 'Anesthesiologist' },
  { value: 'CONSULTANT', label: 'Consultant' },
  { value: 'OTHER', label: 'Other' },
];

const ROLE_LABELS = Object.fromEntries(
  ROLE_OPTIONS.map((option) => [option.value, option.label]),
) as Record<string, string>;

/**
 * Post-admission care team: list the active staff assignments, add a new
 * assignment, or remove one. The admission form only captures the team once at
 * creation, so this is the screen that keeps it current during a long stay.
 */
export function AdmissionCareTeamModal({
  open,
  admissionId,
  onClose,
}: AdmissionCareTeamModalProps) {
  const assignmentsQuery = useStaffAssignments(open ? admissionId : undefined);
  const employeesQuery = useEmployees(1, 500);
  const assignStaff = useAssignStaff();
  const removeStaff = useRemoveStaffAssignment();

  const [employeeId, setEmployeeId] = useState('');
  const [role, setRole] = useState<StaffAssignmentRole>('NURSE');
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [assignmentToRemove, setAssignmentToRemove] = useState<any>(null);

  useEffect(() => {
    if (!open) return;
    setEmployeeId('');
    setRole('NURSE');
    setNotes('');
    setFormError(null);
    setAssignmentToRemove(null);
  }, [open]);

  const assignments = useMemo(
    () => assignmentsQuery.data?.data ?? [],
    [assignmentsQuery.data],
  );
  const employees = useMemo(
    () => employeesQuery.data?.data ?? [],
    [employeesQuery.data],
  );

  // An employee already on the active team is removed from the picker. The
  // backend deactivates the old row and creates a new one, which would look like
  // a silent duplicate from this screen.
  const assignedEmployeeIds = useMemo(
    () => new Set(assignments.map((assignment: any) => assignment.employeeId)),
    [assignments],
  );

  const employeeOptions = useMemo<Option[]>(
    () =>
      employees
        .filter((employee: any) => !assignedEmployeeIds.has(employee.id))
        .map((employee: any) => ({
          value: employee.id,
          label: employee.name || employee.employeeCode || employee.id,
          description: [
            employee.employeeCode,
            employee.designation?.name,
            employee.department?.name,
          ]
            .filter(Boolean)
            .join(' · '),
        })),
    [employees, assignedEmployeeIds],
  );

  if (!open) return null;

  const handleAssign = async () => {
    if (!employeeId) {
      setFormError('Select an employee to add to the care team.');
      return;
    }
    setFormError(null);
    try {
      await assignStaff.mutateAsync({
        admissionId,
        data: { employeeId, role, notes: notes.trim() || undefined },
      });
      toast.success('Care team updated');
      setEmployeeId('');
      setNotes('');
    } catch (err: any) {
      setFormError(
        err?.response?.data?.message ||
          err?.message ||
          'Could not add this employee to the care team.',
      );
    }
  };

  const handleRemove = async () => {
    if (!assignmentToRemove) return;
    try {
      await removeStaff.mutateAsync({
        admissionId,
        assignmentId: assignmentToRemove.id,
      });
      toast.success('Staff assignment removed');
      setAssignmentToRemove(null);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Could not remove this staff assignment.',
      );
    }
  };

  const listError = assignmentsQuery.error
    ? 'Could not load the current care team.'
    : null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        onClick={() => {
          if (!assignStaff.isPending && !removeStaff.isPending) onClose();
        }}
      >
        <div
          className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-xl"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Users size={17} />
              </span>
              <div>
                <h2 className="text-sm font-semibold text-text">Care team</h2>
                <p className="text-[0.6875rem] text-text-muted">
                  Doctors, nurses and other staff assigned to this admission.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={assignStaff.isPending || removeStaff.isPending}
              className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-background hover:text-text disabled:opacity-50"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto p-5">
            {listError && (
              <div className="flex items-start gap-2 rounded-lg border border-danger/20 bg-danger/10 px-3 py-2 text-sm text-danger">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{listError}</span>
              </div>
            )}

            <div>
              <h3 className="mb-2 text-[0.6875rem] font-semibold uppercase tracking-wide text-text-muted">
                Active assignments
              </h3>
              {assignmentsQuery.isLoading ? (
                <div className="flex items-center justify-center rounded-xl border border-border bg-background/40 py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                </div>
              ) : assignments.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border bg-background/40 px-4 py-6 text-center">
                  <Users size={22} className="mx-auto mb-1.5 text-text-muted/50" />
                  <p className="text-sm font-medium text-text">No staff assigned yet</p>
                  <p className="mt-0.5 text-xs text-text-muted">
                    Add the first doctor, nurse or consultant below.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border overflow-hidden rounded-xl border border-border">
                  {assignments.map((assignment: any) => (
                    <div
                      key={assignment.id}
                      className="flex flex-wrap items-start justify-between gap-3 bg-surface px-3.5 py-3"
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate text-sm font-medium text-text">
                            {assignment.employee?.name || 'Unknown employee'}
                          </p>
                          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wide text-primary">
                            {ROLE_LABELS[assignment.role] || assignment.role}
                          </span>
                        </div>
                        <p className="mt-0.5 text-[0.6875rem] text-text-muted">
                          {[
                            assignment.employee?.employeeCode,
                            assignment.employee?.designation?.name,
                            assignment.assignedFrom
                              ? `Since ${formatDateLabel(assignment.assignedFrom)}`
                              : '',
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                        {assignment.notes && (
                          <p className="mt-1 whitespace-pre-wrap text-xs text-text">
                            {assignment.notes}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setAssignmentToRemove(assignment)}
                        disabled={removeStaff.isPending}
                        className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-danger/10 hover:text-danger disabled:opacity-50"
                        title="Remove from care team"
                        aria-label={`Remove ${assignment.employee?.name || 'staff'} from the care team`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-border bg-background/40 p-3.5">
              <h3 className="mb-2 flex items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-text-muted">
                <UserPlus size={13} />
                Add staff
              </h3>
              {formError && (
                <div className="mb-2 flex items-start gap-2 rounded-lg border border-danger/20 bg-danger/10 px-3 py-2 text-xs text-danger">
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>{formError}</span>
                </div>
              )}
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-text">
                    Employee <span className="text-danger">*</span>
                  </label>
                  <SearchableSelect
                    value={employeeId}
                    onChange={setEmployeeId}
                    options={employeeOptions}
                    placeholder="Search employee by name or code…"
                    loading={employeesQuery.isLoading}
                    disabled={assignStaff.isPending}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-text">Role</label>
                  <select
                    value={role}
                    onChange={(event) =>
                      setRole(event.target.value as StaffAssignmentRole)
                    }
                    disabled={assignStaff.isPending}
                    className="h-9 w-full rounded-lg border border-border bg-surface px-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60"
                  >
                    {ROLE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-text">
                    Notes <span className="font-normal text-text-muted">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    maxLength={500}
                    placeholder="e.g. Night shift cover"
                    disabled={assignStaff.isPending}
                    className="h-9 w-full rounded-lg border border-border bg-surface px-2.5 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60"
                  />
                </div>
              </div>
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={handleAssign}
                  disabled={!employeeId || assignStaff.isPending}
                  className="flex items-center gap-2 rounded-lg bg-primary px-3.5 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {assignStaff.isPending ? (
                    <Loader2 size={15} className="animate-spin" />
                  ) : (
                    <Plus size={15} />
                  )}
                  Add to care team
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end border-t border-border bg-background/40 px-5 py-3.5">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-text-muted transition-colors hover:text-text"
            >
              Done
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={assignmentToRemove !== null}
        title="Remove staff assignment?"
        description={
          <>
            Remove{' '}
            <span className="font-medium text-text">
              {assignmentToRemove?.employee?.name || 'this employee'}
            </span>{' '}
            from the care team? The history is kept for audit.
          </>
        }
        confirmLabel="Remove"
        loading={removeStaff.isPending}
        onConfirm={handleRemove}
        onCancel={() => {
          if (!removeStaff.isPending) setAssignmentToRemove(null);
        }}
      />
    </>
  );
}
